import React, { useEffect, useRef, useState, useCallback } from 'react';
import EditableCardContent from '../EditableCardContent.jsx';
import { authApiIdempotent, uploadSourceDocument, getDb, getAuthInstance } from '../../firebase/config.js';
import { doc, collection, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ENV } from '../../config/env.js';
import { CLINICAL_CARD_STYLES, cardStyleLabel } from './clinicalOptions.js';
// A cross-origin CDN URL cannot be used as a Worker script — `new Worker(url)` enforces a
// same-origin-ish restriction that plain fetch()/script-src don't, so pdf.js was silently
// falling back to a slow main-thread "fake worker" (fast enough to read page count, but
// full-resolution page rendering on the main thread hung for a long time). Importing the
// worker file with Vite's `?url` gives a same-origin bundled asset instead.
import pdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

const API_BASE_URL = ENV.apiBaseUrl;

// pdf.js is loaded lazily — only needed once this modal actually opens, mirroring the
// existing lazy-import convention used for jszip/sql.js/pdfjs elsewhere in this codebase.
let pdfjsLibPromise = null;
const loadPdfjs = () => {
    if (!pdfjsLibPromise) {
        pdfjsLibPromise = import('pdfjs-dist').then((pdfjsLib) => {
            pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;
            return pdfjsLib;
        });
    }
    return pdfjsLibPromise;
};

// Fallback only — used before the viewer's actual width has been measured. Real sizing is
// "fit to width" (see containerWidth below), since a fixed scale made wide/landscape slides
// (any PowerPoint, converted via LibreOffice) render wider than the viewer pane, forcing
// horizontal scrolling to read the right side of every slide — and made highlighting seem to
// "cut off" text on the right, since you can't drag a mouse selection past what's currently
// scrolled into view.
const PAGE_RENDER_SCALE = 1.4;
const MIN_FIT_SCALE = 0.4;
const MAX_FIT_SCALE = 3;

// Minimal reimplementation of pdf.js's own text-layer CSS (pdf_viewer.css), scoped under
// .ff-pdf-text-layer so it can't leak into/collide with the rest of the app's styles —
// pulling in the whole pdf_viewer.css would also bring unrelated annotation/find-bar rules.
const TEXT_LAYER_STYLE = `
.ff-pdf-text-layer {
    position: absolute;
    inset: 0;
    overflow: clip;
    opacity: 1;
    line-height: 1;
    text-align: initial;
    transform-origin: 0 0;
}
.ff-pdf-text-layer span, .ff-pdf-text-layer br {
    color: transparent;
    position: absolute;
    white-space: pre;
    cursor: text;
    transform-origin: 0% 0%;
    /* pdf.js's TextLayer class sets these as custom properties per span (not font-size/
       transform directly) and expects the stylesheet to consume them — without this, every
       span silently falls back to the browser's default 16px regardless of the PDF's real
       font size, which is what made highlights render as a thin, uniform-height sliver
       instead of matching each line's actual height. --scale-x corrects horizontal glyph
       spacing to match the canvas-rendered text, without which selection edges land
       slightly off from the visible letters. */
    font-size: var(--font-height, 1em);
    /* --font-height is pdf.js's best-guess em-box for a font it couldn't identify exactly
       (LibreOffice-exported PDFs commonly hit this — the span resolves to a bare "sans-serif"
       fallback, not the real font name). Measured directly against the rendered canvas glyphs:
       that guessed em-box sits a bit high of the actual ink and is noticeably shorter than a
       real ascender-to-descender span. translateY nudges it down to close the gap at top;
       ff-persisted-highlight's padding-bottom (below) covers descenders the box alone misses. */
    transform: scaleX(var(--scale-x, 1)) translateY(5%);
}
.ff-pdf-text-layer ::selection {
    background: rgba(20, 184, 166, 0.45);
}
.ff-pdf-text-layer span.ff-persisted-highlight {
    background: rgba(20, 184, 166, 0.45);
    /* Descender coverage is added per-span as an inline style in handleSelectionChange, not
       here — a fixed CSS value can't know how tightly the next line sits, and overlapped it
       on densely-spaced text, stacking into a visibly darker double highlight. */
    border-radius: 0.15em;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
    /* pdf.js's --scale-x under-corrects the span's width by a small amount that compounds
       character by character — invisible for most of a line, but by the last character
       (often the line's final letter or its trailing punctuation) the box's right edge lands
       just short of it, clipping it out of the highlight. Nothing sits immediately to the
       right of a line's own text, so a small fixed margin here is always safe. */
    padding-right: 0.25em;
}
`;

// One rendered page: canvas + selectable text layer, lazily rendered once scrolled near.
// `enqueueRender` serializes actual rendering across all pages sharing one `pdf` document —
// a short document puts every page within the generous rootMargin at once, so without this
// several DocumentPage instances would call pdf.getPage()/page.render() concurrently against
// the same PDFDocumentProxy; pdf.js's worker message-port protocol doesn't handle that
// cleanly and the whole thing hangs with no error, which is exactly what happened here.
const DocumentPage = ({ pdf, pageNumber, pdfjsLib, enqueueRender, containerWidth }) => {
    const wrapRef = useRef(null);
    const canvasRef = useRef(null);
    const textLayerRef = useRef(null);
    const [dims, setDims] = useState(null);
    const [rendered, setRendered] = useState(false);

    useEffect(() => {
        if (rendered) return;
        const el = wrapRef.current;
        if (!el) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                enqueueRender(renderPage);
                observer.disconnect();
            }
        }, { rootMargin: '800px 0px' });
        observer.observe(el);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rendered]);

    const renderPage = async () => {
        try {
            const page = await pdf.getPage(pageNumber);
            // Fit the page to the actual available viewer width instead of a fixed zoom —
            // a fixed scale works fine for a normal-proportioned PDF page but overflows badly
            // for wide/landscape slides (any PowerPoint deck), forcing horizontal scrolling.
            const unscaled = page.getViewport({ scale: 1 });
            const fitScale = containerWidth > 0 ? containerWidth / unscaled.width : PAGE_RENDER_SCALE;
            const scale = Math.min(Math.max(fitScale, MIN_FIT_SCALE), MAX_FIT_SCALE);
            const viewport = page.getViewport({ scale });
            setDims({ width: viewport.width, height: viewport.height });

            const canvas = canvasRef.current;
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            const ctx = canvas.getContext('2d');
            await page.render({ canvasContext: ctx, viewport }).promise;

            const textContent = await page.getTextContent();
            const textLayer = new pdfjsLib.TextLayer({
                textContentSource: textContent,
                container: textLayerRef.current,
                viewport,
            });
            await textLayer.render();
            setRendered(true);
        } catch (err) {
            console.error('[DocumentPage] render failed for page', pageNumber, err);
            // A single failed page shouldn't take down the whole viewer.
            setRendered(true);
        }
    };

    return (
        <div
            ref={wrapRef}
            data-page-number={pageNumber}
            className="relative mx-auto mb-3 bg-white shadow-lg rounded-sm overflow-hidden"
            style={dims ? { width: dims.width, height: dims.height } : { width: '100%', maxWidth: 700, minHeight: 500 }}
        >
            {!rendered && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-800/40 text-slate-400 text-xs">
                    Loading page {pageNumber}…
                </div>
            )}
            <canvas ref={canvasRef} className="block w-full h-full" />
            <div ref={textLayerRef} className="ff-pdf-text-layer" />
        </div>
    );
};

/**
 * Full-document scroll-and-highlight study modal. The whole document renders like a normal
 * PDF viewer, the user highlights text with native browser text selection (via pdf.js's
 * selectable text layer), and "Make flashcards" can be used repeatedly without closing the
 * modal — cards accumulate in a local queue on the right, editable inline. On "Done", cards
 * save directly into the chosen folder (no separate unfiled "Review Queue" hop) — matches
 * exactly how DeckPage's own upload flow already saves.
 */
const ClinicalDocumentStudyModal = ({
    file,
    sourceDocument, // { id, downloadUrl, fileName } — set instead of `file` when reopening a saved document
    initialPage, // optional: jump straight to this page on open — set when reopening via a card's "View source" link
    folderId, // default/initial target folder — required if `folders` isn't provided (DeckPage always has exactly one)
    folders, // optional: when provided, shows a folder picker (AppHome's workspace, where no folder is fixed yet)
    onFolderCreated, // optional: (folder) => void, called after creating a new folder from the picker
    clinicalSettings,
    onDone,
    onClose,
    onCardsGenerated,
}) => {
    const [pdf, setPdf] = useState(null);
    const [numPages, setNumPages] = useState(0);
    const [pdfjsLib, setPdfjsLib] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [selectedText, setSelectedText] = useState('');
    const [selectedPageNumber, setSelectedPageNumber] = useState(null);
    const [cardCount, setCardCount] = useState(3);
    const [cardStyle, setCardStyle] = useState(clinicalSettings?.cardStyle || CLINICAL_CARD_STYLES[0]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [genError, setGenError] = useState('');
    const [queuedCards, setQueuedCards] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const scrollRef = useRef(null);
    // Measured directly from the viewer pane so pages can be scaled to fit it exactly — see
    // the note on PAGE_RENDER_SCALE above. Re-measured on window resize so pages that haven't
    // rendered yet pick up the new width; already-rendered pages keep their existing size
    // (matches this modal's existing "don't fight an in-progress session" bias elsewhere).
    const [containerWidth, setContainerWidth] = useState(0);
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        // clientWidth includes the container's own horizontal padding (px-4 sm:px-8); back
        // that out plus a little breathing room so pages don't render flush against the edge.
        const measure = () => setContainerWidth(Math.max(0, el.clientWidth - 64));
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);
    const fileName = file?.name || sourceDocument?.fileName || 'document.pdf';
    const showFolderPicker = Array.isArray(folders);

    // DeckPage always passes a single, unambiguous `folderId` with no `folders` array — that
    // one's safe to default to. AppHome's `folderId` prop, by contrast, is ambient state shared
    // with an unrelated "move queue cards to folder" control elsewhere on the page — defaulting
    // to it here silently saved into whatever folder the user last touched there, which felt
    // random. So when a real folder list is present, only pre-fill when there's truly no
    // ambiguity (exactly one folder); otherwise leave it unset and require an explicit choice.
    const [selectedFolderId, setSelectedFolderId] = useState(() => {
        if (!showFolderPicker) return folderId || '';
        return folders.length === 1 ? folders[0].id : '';
    });
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    // Blocking popup shown on Done when cards are queued but no folder has been explicitly
    // chosen — forces a real choice instead of silently picking one or hiding behind small
    // inline text in the sidebar that's easy to miss.
    const [showFolderPrompt, setShowFolderPrompt] = useState(false);

    // Folders can still be loading when this modal first mounts (or the only folder gets
    // created after mount, e.g. right after signup) — pick the folder the moment it becomes
    // the only real option, same "no ambiguity" rule as the initial state above.
    useEffect(() => {
        if (selectedFolderId || !showFolderPicker || !folders?.length) return;
        if (folders.length === 1) setSelectedFolderId(folders[0].id);
    }, [folders, showFolderPicker, selectedFolderId]);

    // Spans currently marked with the persisted highlight (see handleSelectionChange) — kept
    // outside React state since it's a direct DOM-class toggle, not something that needs to
    // trigger a re-render.
    const highlightedSpansRef = useRef([]);
    const clearPersistedHighlight = useCallback(() => {
        highlightedSpansRef.current.forEach((span) => {
            span.classList.remove('ff-persisted-highlight');
            span.style.paddingBottom = '';
        });
        highlightedSpansRef.current = [];
    }, []);

    // Shared across every DocumentPage so their renders queue one-at-a-time instead of firing
    // concurrently against the same pdf.js document — see the note on DocumentPage above.
    const renderQueueRef = useRef(Promise.resolve());
    const enqueueRender = useCallback((fn) => {
        renderQueueRef.current = renderQueueRef.current.then(fn, fn);
        return renderQueueRef.current;
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const lib = await loadPdfjs();
                if (cancelled) return;
                setPdfjsLib(lib);
                const source = file ? { data: await file.arrayBuffer() } : { url: sourceDocument.downloadUrl };
                const loaded = await lib.getDocument(source).promise;
                if (cancelled) return;
                setPdf(loaded);
                setNumPages(loaded.numPages);
            } catch (err) {
                if (!cancelled) setLoadError(err?.message || 'Failed to open document.');
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file, sourceDocument]);

    // Jump straight to the page a "View source" link pointed at. Page wrapper divs (with
    // data-page-number) all mount as soon as numPages is known — only their canvas contents
    // render lazily via enqueueRender — so it's safe to scroll as soon as the target wrapper
    // exists, no need to wait for that page's actual render to finish.
    useEffect(() => {
        if (!initialPage || !numPages || !scrollRef.current) return;
        const target = scrollRef.current.querySelector(`[data-page-number="${initialPage}"]`);
        if (target) target.scrollIntoView({ block: 'start' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPage, numPages]);

    // The native browser Selection collapses the instant the user clicks anywhere else. The
    // card-count stepper and style buttons below use onMouseDown+preventDefault so they never
    // trigger that in the first place (the real fix) — this persisted-highlight marking is a
    // second line of defense for anything else that steals focus (e.g. scrolling), so the
    // highlight never contradicts what selectedText actually remembers.
    const handleSelectionChange = useCallback(() => {
        const sel = window.getSelection();
        const text = sel?.toString()?.trim() || '';
        setSelectedText(text);
        clearPersistedHighlight();
        if (text && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            const spans = document.querySelectorAll('.ff-pdf-text-layer span');
            const marked = [];
            spans.forEach((span) => {
                if (range.intersectsNode(span)) {
                    span.classList.add('ff-persisted-highlight');
                    marked.push(span);
                }
            });
            highlightedSpansRef.current = marked;
            // Which page the selection actually came from — carried through to the generated
            // card as sourcePage, so "View source" can jump straight to the right page instead
            // of just opening the document at page 1.
            const pageEl = marked[0]?.closest('[data-page-number]');
            setSelectedPageNumber(pageEl ? Number(pageEl.getAttribute('data-page-number')) : null);
            // The persisted-highlight class is now painting this exact range, so drop the
            // native selection right away — otherwise ::selection keeps painting the same
            // teal on top of it until the user clicks elsewhere, and two stacked
            // semi-transparent layers show up as a visibly darker "double" highlight.
            sel.removeAllRanges();

            // Extend each highlighted line downward to cover descenders — but only as far as
            // the real gap to the next line allows. A fixed CSS padding-bottom can't know that,
            // and on densely-spaced text it ate into the line below, stacking into a visibly
            // darker double highlight where the two overlapped.
            const pageSpansCache = new Map();
            marked.forEach((span) => {
                const page = span.closest('.ff-pdf-text-layer');
                if (!page) return;
                const rect = span.getBoundingClientRect();
                const fontSize = parseFloat(getComputedStyle(span).fontSize) || 16;
                const desiredPx = fontSize * 0.4;
                if (!pageSpansCache.has(page)) pageSpansCache.set(page, [...page.querySelectorAll('span')]);
                const pageSpans = pageSpansCache.get(page);
                const idx = pageSpans.indexOf(span);
                let nextTop = null;
                for (let i = idx + 1; i < pageSpans.length; i++) {
                    const t = pageSpans[i].getBoundingClientRect().top;
                    if (t > rect.top + 2) { nextTop = t; break; }
                }
                const gap = nextTop != null ? nextTop - rect.bottom : Infinity;
                // Leave at least 30% of the gap as a visible seam between adjacent lines even
                // in the tightest case, so highlighted lines never visually touch.
                const safePx = Math.max(0, Math.min(desiredPx, gap * 0.7));
                span.style.paddingBottom = `${safePx}px`;
            });
        }
    }, [clearPersistedHighlight]);

    // Prevents the mousedown default (which is what actually steals focus/collapses the
    // selection) on any control that shouldn't disturb an in-progress highlight.
    const preserveSelection = (e) => e.preventDefault();

    const handleMakeFlashcards = async () => {
        if (!selectedText || isGenerating) return;
        setIsGenerating(true);
        setGenError('');
        try {
            const count = Math.max(1, Math.min(10, Number(cardCount) || 3));
            const data = await authApiIdempotent(`${API_BASE_URL}/generate-flashcards-from-image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: selectedText,
                    cardCount: count,
                    clinical: { examType: clinicalSettings?.examType, cardStyle },
                }),
            });
            const newCards = (data.cards || []).map((c) => ({
                id: `doc-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                question: c.question || c.q || c.prompt || '',
                answer: c.answer || c.a || '',
                isFlagged: false,
                lastViewed: null,
                sourcePage: selectedPageNumber,
            }));
            setQueuedCards((prev) => [...newCards, ...prev]);
            window.getSelection()?.removeAllRanges();
            clearPersistedHighlight();
            setSelectedText('');
            // Credits are spent server-side the moment generation succeeds (not at Done, and
            // not per-card in the merged queue) — usage/free-trial bookkeeping fires here to
            // match, same as the main upload flow's per-generation accounting.
            onCardsGenerated?.(newCards.length);
        } catch (err) {
            setGenError(err?.message || 'Failed to generate flashcards from the selected text.');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSaveCard = (updated) => {
        setQueuedCards((prev) => prev.map((c) => (c.id === updated.id ? { ...c, question: updated.question, answer: updated.answer } : c)));
        setEditingId(null);
    };

    const handleRemoveCard = (id) => {
        setQueuedCards((prev) => prev.filter((c) => c.id !== id));
    };

    const handleCreateFolder = async () => {
        const name = newFolderName.trim();
        if (!name) return;
        const uid = getAuthInstance()?.currentUser?.uid;
        const db = getDb();
        if (!uid || !db) { setSaveError('Not signed in.'); return; }
        try {
            const ref = doc(collection(db, 'users', uid, 'folders'));
            await setDoc(ref, { name, createdAt: serverTimestamp(), updatedAt: serverTimestamp(), cardCount: 0 });
            setSelectedFolderId(ref.id);
            setIsCreatingFolder(false);
            setNewFolderName('');
            onFolderCreated?.({ id: ref.id, name });
        } catch (err) {
            setSaveError(err?.message || 'Failed to create folder.');
        }
    };

    // Any exit path (Done or the X) preserves already-generated cards — they cost real
    // credits to make, so silently discarding them on close would be a real loss, not just
    // a UX inconvenience. If there's nothing queued, there's nothing to save, so a folder
    // isn't required to close.
    const finishAndClose = async () => {
        setSaveError('');
        if (queuedCards.length > 0 && !selectedFolderId) {
            setShowFolderPrompt(true);
            return;
        }
        setIsSaving(true);
        try {
            const uid = getAuthInstance()?.currentUser?.uid;
            const db = getDb();
            if (queuedCards.length > 0 && uid && db && selectedFolderId) {
                // Persist the source document (new uploads only) BEFORE saving cards, so each
                // card can be written with a sourceDocumentId pointing at it — this is what
                // lets a card later link back to the exact material it came from. Best-effort:
                // a failed upload shouldn't block saving the cards themselves, it just means
                // those cards save without a source link (same as before this existed).
                let linkedDocId = sourceDocument?.id ?? null;
                if (file) {
                    try {
                        const docRef = doc(collection(db, 'users', uid, 'folders', selectedFolderId, 'sourceDocuments'));
                        const downloadUrl = await uploadSourceDocument(uid, selectedFolderId, file, docRef.id);
                        await setDoc(docRef, {
                            fileName: file.name,
                            mimeType: file.type || 'application/pdf',
                            downloadUrl,
                            pageCount: numPages,
                            createdAt: serverTimestamp(),
                        });
                        linkedDocId = docRef.id;
                    } catch (_) { /* best-effort */ }
                }

                // Write straight into the folder — same shape/pattern as Anki import and every
                // other direct-to-folder save in this app. No separate "unfiled queue" hop.
                await Promise.all(queuedCards.map(({ question, answer, sourcePage }) => {
                    const cardRef = doc(collection(db, 'users', uid, 'folders', selectedFolderId, 'cards'));
                    return setDoc(cardRef, {
                        question, answer, isFlagged: false, imageUrl: null, clinicalQuestionId: null,
                        sourceDocumentId: linkedDocId, sourceDocumentPage: sourcePage ?? null,
                        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
                    });
                }));
                updateDoc(doc(db, 'users', uid, 'folders', selectedFolderId), {
                    cardCount: (folders?.find((f) => f.id === selectedFolderId)?.cardCount ?? 0) + queuedCards.length,
                    updatedAt: serverTimestamp(),
                }).catch(() => {});
            }
            onDone?.({ folderId: selectedFolderId || null, count: queuedCards.length });
        } catch (err) {
            setIsSaving(false);
            setSaveError(err?.message || 'Failed to save flashcards — try again.');
            return;
        }
        setIsSaving(false);
    };

    return (
        <div className="fixed inset-0 z-[60] bg-slate-950/90 backdrop-blur-sm flex flex-col">
            {/* Header */}
            <div className="shrink-0 flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10 bg-slate-900">
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{fileName}</p>
                    <p className="text-xs text-slate-500">{numPages ? `${numPages} page${numPages === 1 ? '' : 's'}` : 'Loading…'} · Highlight text, then make flashcards — repeat as needed</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {queuedCards.length > 0 && (
                        <span className="text-xs text-teal-300 bg-teal-500/10 rounded-full px-2.5 py-1">{queuedCards.length} card{queuedCards.length === 1 ? '' : 's'} made</span>
                    )}
                    <button
                        type="button"
                        onClick={finishAndClose}
                        disabled={isSaving || isGenerating}
                        title={isGenerating ? 'Wait for the current generation to finish' : undefined}
                        className="rounded-xl px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 disabled:opacity-50 transition"
                    >
                        {isSaving ? 'Saving…' : isGenerating ? 'Generating…' : 'Done'}
                    </button>
                </div>
            </div>

            <div className="flex-1 min-h-0 flex">
                {/* Document viewer */}
                <div className="flex-1 min-w-0 flex flex-col">
                    <div ref={scrollRef} onMouseUp={handleSelectionChange} className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-8 py-6 bg-slate-950">
                        <style dangerouslySetInnerHTML={{ __html: TEXT_LAYER_STYLE }} />
                        {loadError ? (
                            <p className="text-center text-sm text-rose-300 mt-10">{loadError}</p>
                        ) : !pdf ? (
                            <p className="text-center text-sm text-slate-500 mt-10">Loading document…</p>
                        ) : (
                            Array.from({ length: numPages }, (_, i) => (
                                <DocumentPage key={i + 1} pdf={pdf} pageNumber={i + 1} pdfjsLib={pdfjsLib} enqueueRender={enqueueRender} containerWidth={containerWidth} />
                            ))
                        )}
                    </div>

                    {/* Selection action bar */}
                    <div className="shrink-0 border-t border-white/10 bg-slate-900 px-4 sm:px-6 py-3 space-y-2.5">
                        {genError && <p className="text-xs text-rose-300">{genError}</p>}
                        <p className="text-xs text-slate-400 truncate">
                            {selectedText ? <span className="text-slate-200">"{selectedText.slice(0, 100)}{selectedText.length > 100 ? '…' : ''}"</span> : 'Highlight text above to make flashcards from it'}
                        </p>
                        <div className="flex items-center gap-3 flex-wrap">
                            {/* Card style — segmented buttons, mousedown-guarded so picking a style never
                                collapses the current highlight */}
                            <div className="inline-flex rounded-lg bg-slate-800/70 ring-1 ring-white/10 p-0.5">
                                {CLINICAL_CARD_STYLES.map((style) => (
                                    <button
                                        key={style}
                                        type="button"
                                        onMouseDown={preserveSelection}
                                        onClick={() => setCardStyle(style)}
                                        className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition ${cardStyle === style ? 'bg-teal-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                                    >
                                        {cardStyleLabel(style, clinicalSettings?.examType)}
                                    </button>
                                ))}
                            </div>

                            {/* Card count stepper — +/- instead of a native number input, so clicking it
                                never steals focus/collapses the selection like clicking into a text field would */}
                            <div className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800/70 ring-1 ring-white/10 px-1 py-0.5">
                                <button
                                    type="button"
                                    onMouseDown={preserveSelection}
                                    onClick={() => setCardCount((n) => Math.max(1, Number(n) - 1))}
                                    className="h-6 w-6 flex items-center justify-center rounded text-slate-300 hover:bg-white/10"
                                >
                                    −
                                </button>
                                <span className="w-5 text-center text-sm text-white tabular-nums">{cardCount}</span>
                                <button
                                    type="button"
                                    onMouseDown={preserveSelection}
                                    onClick={() => setCardCount((n) => Math.min(10, Number(n) + 1))}
                                    className="h-6 w-6 flex items-center justify-center rounded text-slate-300 hover:bg-white/10"
                                >
                                    +
                                </button>
                                <span className="text-[10px] text-slate-500 pr-1">cards</span>
                            </div>

                            <button
                                type="button"
                                onMouseDown={preserveSelection}
                                onClick={handleMakeFlashcards}
                                disabled={!selectedText || isGenerating}
                                className="ml-auto shrink-0 rounded-xl px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 disabled:opacity-40 transition"
                            >
                                {isGenerating ? 'Generating…' : 'Make flashcards'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* In-modal queue */}
                <div className="w-full max-w-sm shrink-0 border-l border-white/10 bg-slate-900 flex flex-col">
                    <div className="shrink-0 px-4 py-3 border-b border-white/10 space-y-2">
                        <p className="text-sm font-semibold text-white">Flashcards ({queuedCards.length})</p>
                        {showFolderPicker && (
                            <div>
                                <label className={`block text-[10px] uppercase tracking-wider mb-1 ${!selectedFolderId ? 'text-amber-400 font-semibold' : 'text-slate-500'}`}>
                                    Save to folder{!selectedFolderId ? ' — required' : ''}
                                </label>
                                {isCreatingFolder ? (
                                    <div className="flex items-center gap-1.5">
                                        <input
                                            autoFocus
                                            value={newFolderName}
                                            onChange={(e) => setNewFolderName(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') setIsCreatingFolder(false); }}
                                            placeholder="New folder name"
                                            className="flex-1 min-w-0 rounded-lg bg-slate-800/70 border border-white/10 px-2 py-1.5 text-xs text-white"
                                        />
                                        <button type="button" onClick={handleCreateFolder} className="shrink-0 rounded-lg px-2 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-500">Add</button>
                                        <button type="button" onClick={() => setIsCreatingFolder(false)} className="shrink-0 text-xs text-slate-400 hover:text-slate-200">Cancel</button>
                                    </div>
                                ) : (
                                    <select
                                        value={selectedFolderId}
                                        onChange={(e) => {
                                            if (e.target.value === '__new__') { setIsCreatingFolder(true); return; }
                                            setSelectedFolderId(e.target.value);
                                        }}
                                        className={`w-full rounded-lg bg-slate-800/70 border px-2 py-1.5 text-xs text-white ${!selectedFolderId ? 'border-amber-500/60 ring-1 ring-amber-500/30' : 'border-white/10'}`}
                                    >
                                        <option value="" disabled>Choose a folder…</option>
                                        {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                                        <option value="__new__">+ New folder</option>
                                    </select>
                                )}
                            </div>
                        )}
                        {saveError && <p className="text-xs text-rose-300">{saveError}</p>}
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-2">
                        {queuedCards.length === 0 && (
                            <p className="text-xs text-slate-500 px-1">Cards you make will show up here — nothing is saved until you click Done.</p>
                        )}
                        {queuedCards.map((card) => (
                            <div key={card.id} className="rounded-xl border border-white/10 bg-slate-800/50 p-3">
                                {editingId === card.id ? (
                                    <EditableCardContent card={card} onSave={handleSaveCard} onCancel={() => setEditingId(null)} />
                                ) : (
                                    <>
                                        <p className="text-sm text-slate-100 font-medium">{card.question}</p>
                                        <p className="text-xs text-slate-400 mt-1">{typeof card.answer === 'string' ? card.answer : JSON.stringify(card.answer)}</p>
                                        <div className="mt-2 flex items-center gap-3">
                                            <button type="button" onClick={() => setEditingId(card.id)} className="text-xs text-teal-300 hover:text-teal-200">Edit</button>
                                            <button type="button" onClick={() => handleRemoveCard(card.id)} className="text-xs text-rose-300 hover:text-rose-200">Remove</button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {showFolderPrompt && (
                <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-sm rounded-2xl bg-slate-900 border border-white/10 p-5 space-y-4">
                        <div>
                            <p className="text-sm font-semibold text-white">
                                Save {queuedCards.length} flashcard{queuedCards.length === 1 ? '' : 's'} to which folder?
                            </p>
                            <p className="text-xs text-slate-500 mt-1">Choose an existing folder or create a new one.</p>
                        </div>
                        {isCreatingFolder ? (
                            <div className="flex items-center gap-1.5">
                                <input
                                    autoFocus
                                    value={newFolderName}
                                    onChange={(e) => setNewFolderName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') setIsCreatingFolder(false); }}
                                    placeholder="New folder name"
                                    className="flex-1 min-w-0 rounded-lg bg-slate-800/70 border border-white/10 px-2.5 py-2 text-sm text-white"
                                />
                                <button type="button" onClick={handleCreateFolder} className="shrink-0 rounded-lg px-3 py-2 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-500">Add</button>
                                <button type="button" onClick={() => setIsCreatingFolder(false)} className="shrink-0 text-sm text-slate-400 hover:text-slate-200">Cancel</button>
                            </div>
                        ) : (
                            <select
                                autoFocus
                                value={selectedFolderId}
                                onChange={(e) => {
                                    if (e.target.value === '__new__') { setIsCreatingFolder(true); return; }
                                    setSelectedFolderId(e.target.value);
                                }}
                                className="w-full rounded-lg bg-slate-800/70 border border-white/10 px-3 py-2 text-sm text-white"
                            >
                                <option value="" disabled>Choose a folder…</option>
                                {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                                <option value="__new__">+ New folder</option>
                            </select>
                        )}
                        {saveError && <p className="text-xs text-rose-300">{saveError}</p>}
                        <div className="flex items-center justify-end gap-2 pt-1">
                            <button type="button" onClick={() => setShowFolderPrompt(false)} className="rounded-xl px-3.5 py-2 text-sm text-slate-300 hover:text-white">
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={!selectedFolderId}
                                onClick={() => { setShowFolderPrompt(false); finishAndClose(); }}
                                className="rounded-xl px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 disabled:opacity-40"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClinicalDocumentStudyModal;
